import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Auth.css';
import Logo from '../../assets/Logo.png';

const Login = () => {
    const [formData, setFormData] = useState({
        identifier: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const redirectFrom = searchParams.get('from') || location.state?.from || '/';

    useEffect(() => {
        setFormData({ identifier: '', password: '' });
        setErrors({});
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.identifier.trim()) newErrors.identifier = 'Email or username is required';
        if (!formData.password) newErrors.password = 'Password is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await login(formData);
            toast.success(`Welcome back, ${response.user.name || response.user.email}!`);
            if (response.user.role === 'Admin') {
                navigate('/admin/dashboard');
            } else {
                navigate(redirectFrom, { replace: true });
            }
        } catch (error) {
            toast.error(error.message || 'Login failed. Please try again.');
          
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-logo">
                    <img src={Logo} alt="Ginni Fashion Studio" />
                </div>

                <h1 className="auth-title">Login to your Account</h1>

                <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                    <div className="form-group">
                        <input
                            type="text"
                            name="identifier"
                            value={formData.identifier}
                            onChange={handleChange}
                            className={`form-input ${errors.identifier ? 'error' : ''}`}
                            placeholder="Username or Email"
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            data-lpignore="true"
                            data-form-type="other"
                        />
                        {errors.identifier && (
                            <div className="error-message">{errors.identifier}</div>
                        )}
                    </div>

                    <div className="form-group">
                        {/* ── password wrapper ── */}
                        <div className="password-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-input ${errors.password ? 'error' : ''}`}
                                placeholder="Password"
                                autoComplete="new-password"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                data-lpignore="true"
                                data-form-type="other"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(prev => !prev)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                tabIndex={-1}
                            >
                                {showPassword ? (
                                    /* Eye-off SVG */
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20
                                                 C7 20 2.73 16.39 1 12a10.07 10.07 0 0 1
                                                 2.06-3.94"/>
                                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4
                                                 c5 0 9.27 3.61 11 8a9.19 9.19 0 0 1
                                                 -1.06 2.06"/>
                                        <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                ) : (
                                    /* Eye SVG */
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <div className="error-message">{errors.password}</div>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="error-message" style={{ textAlign: 'center', marginBottom: '15px' }}>
                            {errors.submit}
                        </div>
                    )}

                    <button type="submit" className="auth-button" disabled={loading}>
                        {loading ? (
                            <><span className="loading-spinner"></span>Signing In...</>
                        ) : 'Login'}
                    </button>

                    <div className="auth-link forgot-password">
                        <Link to="/forgot-password">Forgot Password?</Link>
                    </div>
                </form>

                <div className="auth-link signup-link">
                    Don't have an account? <Link to={`/signup?from=${encodeURIComponent(redirectFrom)}`}>Sign up</Link>
                </div>
            </div>
        </div>
    );
};

export default Login;